import type { FilterAndSortProps } from "@ext/markdown/elements/table/edit/model/tableTypes";
import { useSyncExternalStore } from "react";

// Any stable object identifying one table instance; compared by reference.
export type TablePropsOwner = object;

const store = new Map<number, FilterAndSortProps>();
const owners = new Map<number, TablePropsOwner>();
const listeners = new Set<() => void>();

const subscribe = (callback: () => void) => {
	listeners.add(callback);
	return () => listeners.delete(callback);
};

const emit = () => {
	listeners.forEach((l) => l());
};

const updateTableProps = (tablePos: number, props: FilterAndSortProps, owner: TablePropsOwner) => {
	store.set(tablePos, props);
	owners.set(tablePos, owner);
	emit();
};

// Entries are keyed by the table's position, which moves with every edit above the table: the
// owner drops its old key so the map does not keep one stale entry per keystroke. Only the owner
// may drop it: a table inserted right above another one can write that position before the lower
// table moves off it, and the lower table's cleanup must not delete the new table's entry.
const removeTableProps = (tablePos: number, owner: TablePropsOwner) => {
	if (owners.get(tablePos) !== owner) return;
	owners.delete(tablePos);
	store.delete(tablePos);
	emit();
};

const useTableProps = (tablePos: number) => {
	return useSyncExternalStore(subscribe, () => store.get(tablePos));
};

const useFilterAndSortProps = (tablePos: number) => {
	return useSyncExternalStore(subscribe, () => store.get(tablePos));
};

const tablePropsStore = {
	removeTableProps,
	updateTableProps,
	useFilterAndSortProps,
	useTableProps,
};

export default tablePropsStore;
